'use client';

import type { ProductInformation } from '@dealerportal/common';
import { useTranslations } from '@dealerportal/i18n/client';
import { remapBrandName } from '@dealerportal/utilities';
import classNames from 'classnames';
import { ProductTags } from '../ProductCard/ProductTags';

type ProductDetailsHeaderProps = {
  productInfo: ProductInformation;
  isMobileDevice: boolean;
  hideBrandColorSkuTitleInPDP?: boolean;
  tags: string[];
};

const ProductTitle = ({
  productName,
  brandName,
  hideBrandColorSkuTitleInPDP,
  t,
}: {
  productName: string;
  brandName: string;
  hideBrandColorSkuTitleInPDP?: boolean;
  t: (key: string) => string;
}) => (
  <h1
    data-testid="headline"
    className="pdp__info-headline !ui-text-3xl !ui-text-left"
  >
    <span className="pdp__info-line">{productName}</span>
    {/* CiC cabinet items often carry no brand group; "B12L by" reads broken. */}
    {!hideBrandColorSkuTitleInPDP && brandName && (
      <>
        <span className="pdp__info-connecting-word"> {t('by')} </span>
        <span className="pdp__info-brand">{remapBrandName(brandName)}</span>
      </>
    )}
  </h1>
);

const ProductSpecs = ({
  productType,
  sku,
  isMobileDevice,
  hideBrandColorSkuTitleInPDP,
  t,
}: {
  productType: string;
  sku: string;
  isMobileDevice: boolean;
  hideBrandColorSkuTitleInPDP?: boolean;
  t: (key: string) => string;
}) => (
  <div
    className={classNames(
      'ui-flex ui-items-center ui-gap-2 ui-text-xs ui-text-gray-500',
      {
        'ui-mt-1': isMobileDevice,
        'ui-mt-2': !isMobileDevice,
      }
    )}
  >
    <span className="pdp__simple-specs-type" data-testid="product-type">
      {productType}
    </span>
    <span className="pdp__simple-specs-divider">|</span>
    {!hideBrandColorSkuTitleInPDP && (
      <span className="pdp__simple-specs-sku" data-testid="sku">
        {t('sku')} {sku}
      </span>
    )}
  </div>
);

const TagsSection = ({ tags }: { tags: string[] }) => (
  <div className="product-tags ui-mt-3 -ui-ml-1">
    {tags?.length ? <ProductTags isOpen={true} tags={tags} /> : null}
  </div>
);

export const ProductDetailsHeader = ({
  productInfo,
  isMobileDevice,
  hideBrandColorSkuTitleInPDP,
  tags,
}: ProductDetailsHeaderProps) => {
  const t = useTranslations('products');

  const commonProps = {
    productName: productInfo.productName,
    brandName: productInfo.brandName,
    productType: productInfo.productType,
    sku: productInfo.sku,
    isMobileDevice,
    hideBrandColorSkuTitleInPDP,
    t,
  };

  if (isMobileDevice) {
    return (
      <div className="pdp__info --mobile ui-mb-6">
        <ProductTitle {...commonProps} />
        <ProductSpecs {...commonProps} />
        <TagsSection tags={tags} />
      </div>
    );
  }

  return (
    <div className="pdp__info --desktop">
      <ProductTitle {...commonProps} />
      <ProductSpecs {...commonProps} />
      <TagsSection tags={tags} />
    </div>
  );
};
